import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Button from 'components/field/Button';
import CopyComponent from 'components/field/CopyComponent';
import CurrencyInput from 'components/field/CurrencyInput';
import DateWithTime from 'components/field/DateWithTime';
import ImageViewer from 'components/field/ImageViewer';
import IntegerInput from 'components/field/IntegerInput';
import Status from 'components/field/Status';
import StringInput from 'components/field/StringInput';
import FormItem from 'components/form/FormItem';
import Icon from 'components/global/Icon';
import ModuleToolTipContainer from 'components/global/ModuleToolTipContainer';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';

import PATHS from 'utils/paths';

import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';

const defaultColSpan = {
	md: 6
};

export const ShippingDetails = ({ isAdmin, formMode, orderType }) => {
	const navigate = useNavigate();
	const userType = useSelector((state) => state.user.userType);

	const formMethods = useFormContext();
	const {
		setValue,
		watch,
		getValues,
		formState: { errors, isLoading: isFormLoading, isSubmitting }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;
	const isImportOrder = orderType === 'import';

	const reasonName = useMemo(() => {
		const status = getValues('status');

		switch (status) {
			case 'canceled':
				return 'cancelReason';
			case 'shipmentOnHoldWarehouse':
				return 'onHoldReason';
			case 'returned':
				return 'returnReason';
			default:
				return null;
		}
	}, []);

	const commentName = useMemo(() => {
		const status = getValues('status');

		switch (status) {
			case 'canceled':
				return 'cancelComment';
			case 'shipmentOnHoldWarehouse':
				return 'onHoldComment';
			default:
				return null;
		}
	}, []);

	const watchedOrderDocs = watch('orderDocs');

	return (
		<>
			<FormItem colSpan={defaultColSpan} label="deliveryCompany">
				<StringInput name="deliveryCompany" disabled />
			</FormItem>
			<FormItem colSpan={defaultColSpan} label="shipmentID">
				<StringInput name="shipmentID" disabled />
			</FormItem>

			{!isImportOrder && (
				<FormItem colSpan={defaultColSpan} label="deliverySlotFrom" isLoading={isLoading}>
					<DateWithTime
						name="deliverySlotDate"
						value={watch('deliverySlotDate')}
						errorMessage={
							errors.deliverySlotDate?.date?.message ||
							errors.deliverySlotDate?.from?.message ||
							errors.deliverySlotDate?.to?.message
						}
						disabled={formMode === 'view' || (!isAdmin && userType === 'customerServiceUser')}
						setValue={(value) => {
							setValue('deliverySlotDate', value, { shouldValidate: true, shouldDirty: true });
						}}
					/>
				</FormItem>
			)}

			<FormItem colSpan={defaultColSpan} label="shippingDescription" isLoading={isLoading}>
				<StringInput
					name="shippingDescription"
					disabled={formMode === 'view' || (!isAdmin && userType === 'customerServiceUser')}
				/>
			</FormItem>

			{!isImportOrder && (
				<FormItem
					colSpan={defaultColSpan}
					label="drivingDistance"
					roleName="OMSModule"
					customLabelContent={
						<Tooltip
							timeout={100}
							placement="bottom"
							color={'var(--white)'}
							title={
								<div>
									<ModuleToolTipContainer description="drivingDistance.description" />

									<Button
										className="mt-3 mx-auto"
										label="autocorrectorSettings"
										size="x-small"
										variant="blue"
										onClick={() => navigate(PATHS.AUTOCORRECTOR)}
									/>
								</div>
							}>
							<Icon icon={faCircleInfo} />
						</Tooltip>
					}>
					<IntegerInput disabled={formMode !== 'add'} name="drivingDistance" unit="m" />
				</FormItem>
			)}

			<FormItem colSpan={defaultColSpan} label="status">
				<Status value={getValues('status')} />
			</FormItem>

			{reasonName !== null && (
				<FormItem colSpan={defaultColSpan} label={reasonName}>
					<StringInput name={reasonName} disabled />
				</FormItem>
			)}

			{commentName !== null && (
				<FormItem colSpan={defaultColSpan} label={commentName}>
					<StringInput name={commentName} disabled />
				</FormItem>
			)}

			<FormItem colSpan={defaultColSpan} label="trackingURL">
				<CopyComponent value={watch('trackingURL')} />
			</FormItem>

			{watch(`isInternationalOrder`) && isAdmin ? (
				<FormItem colSpan={defaultColSpan} label="customValue">
					<CurrencyInput
						name={`customValue`}
						value={watch(`customCurrency`)}
						setValue={(value) => {
							setValue(`customCurrency`, value, {
								shouldValidate: true,
								shouldDirty: true
							});
						}}
					/>
				</FormItem>
			) : null}

			{watchedOrderDocs?.length > 0 && (
				<FormItem label="shippingDetails.orderDocs">
					<div className="row row-cols-auto">
						{watchedOrderDocs.map((doc) => (
							<div key={doc.id}>
								<ImageViewer imageUrl={doc.url} />
								<Typography variant="p2" fullWidth align="center" className="my-2">
									{doc.label}
								</Typography>
							</div>
						))}
					</div>
				</FormItem>
			)}
		</>
	);
};
